/**
 * LA COLA DE FOTOS QUE ESPERAN SEÑAL.
 *
 * Una foto de la visita se toma en la obra, muchas veces sin internet. En vez
 * de perderse —o de bloquear al técnico hasta que haya señal— se guarda aquí y
 * sube sola cuando la conexión vuelve.
 *
 * Vive en IndexedDB, que es el almacenamiento PRIVADO de la app: en un teléfono
 * normal no aparece en la galería ni en el explorador de archivos, así que no
 * se puede abrir ni cambiar desde afuera. La foto ya viene sellada (fecha, hora,
 * GPS quemados en la imagen) y con su hash, calculado al disparar; los dos se
 * guardan tal cual para que la evidencia no dependa de cuándo suba.
 *
 * Es a propósito una capa mínima sobre IndexedDB, sin librería: abrir, encolar,
 * listar, contar y quitar. Nada más hace falta.
 */

const BASE = 'mega-fotos'
const ALMACEN = 'pendientes'
const VERSION = 1

export type FotoEnCola = {
  /** Identificador local, único, que también será el nombre en el bucket. */
  id: string
  ordenServicioId: string
  /** La imagen sellada, tal como se subirá. */
  blob: Blob
  tipo: string
  /** SHA-256 de la imagen, calculado al disparar. */
  hash: string
  /** Hora de la obra: cuándo se tomó. */
  tomadaEn: string
  /** Cuándo entró a la cola (para subir en orden). */
  creado: number
}

let promesaBase: Promise<IDBDatabase> | null = null

/**
 * Lo mismo que ya hace la cola de acciones (colaDeAcciones.ts): abrir
 * IndexedDB puede no terminar nunca si otra pestaña la tiene bloqueada, y sin
 * plazo la cámara se quedaba en «guardando» toda la sesión (0324).
 */
function conPlazo<T>(promesa: Promise<T>, ms: number, queEspera: string): Promise<T> {
  return Promise.race([
    promesa,
    new Promise<T>((_, rechazar) =>
      setTimeout(() => rechazar(new Error(`${queEspera} no respondió a tiempo.`)), ms),
    ),
  ])
}

function abrir(): Promise<IDBDatabase> {
  if (promesaBase) return promesaBase

  promesaBase = conPlazo(new Promise<IDBDatabase>((resolver, rechazar) => {
    const solicitud = indexedDB.open(BASE, VERSION)

    solicitud.onupgradeneeded = () => {
      const db = solicitud.result
      if (!db.objectStoreNames.contains(ALMACEN)) {
        const almacen = db.createObjectStore(ALMACEN, { keyPath: 'id' })
        // Por orden se lista y se sube: la más vieja primero.
        almacen.createIndex('orden', 'ordenServicioId', { unique: false })
        almacen.createIndex('creado', 'creado', { unique: false })
      }
    }

    solicitud.onsuccess = () => resolver(solicitud.result)
    solicitud.onerror = () => rechazar(solicitud.error ?? new Error('No se pudo abrir la cola.'))
    solicitud.onblocked = () =>
      rechazar(new Error('El almacenamiento del teléfono está ocupado por otra pestaña.'))
  }), 5000, 'El almacenamiento de fotos del teléfono')

  // Si falló, el siguiente intento vuelve a probar en vez de heredar el fallo.
  promesaBase.catch(() => {
    promesaBase = null
  })

  return promesaBase
}

/** Promesa a partir de una transacción de escritura que termina de verdad. */
function conEscritura<T>(fn: (almacen: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return abrir().then(
    (db) =>
      new Promise<T>((resolver, rechazar) => {
        const tx = db.transaction(ALMACEN, 'readwrite')
        const solicitud = fn(tx.objectStore(ALMACEN))
        // Se resuelve con `oncomplete`, no con el `onsuccess` de la solicitud:
        // así la promesa espera a que el dato quede escrito de verdad en disco.
        tx.oncomplete = () => resolver(solicitud.result)
        tx.onerror = () => rechazar(tx.error ?? new Error('Falló la escritura en la cola.'))
        tx.onabort = () => rechazar(tx.error ?? new Error('Se abortó la escritura en la cola.'))
      }),
  )
}

function leer<T>(fn: (almacen: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return abrir().then(
    (db) =>
      new Promise<T>((resolver, rechazar) => {
        const solicitud = fn(db.transaction(ALMACEN, 'readonly').objectStore(ALMACEN))
        solicitud.onsuccess = () => resolver(solicitud.result)
        solicitud.onerror = () => rechazar(solicitud.error ?? new Error('Falló la lectura de la cola.'))
      }),
  )
}

/** Mete una foto sellada a la cola. */
export async function encolarFoto(foto: FotoEnCola): Promise<void> {
  await conEscritura((a) => a.put(foto))
}

/** Lo que espera subir de una orden, de lo más viejo a lo más nuevo. */
export async function fotosEnCola(ordenServicioId: string): Promise<FotoEnCola[]> {
  const todas = await leer<FotoEnCola[]>((a) => a.getAll())
  return todas
    .filter((f) => f.ordenServicioId === ordenServicioId)
    .sort((a, b) => a.creado - b.creado)
}

/** Todo lo que espera subir, de cualquier orden. Para el sincronizador. */
export async function todaLaCola(): Promise<FotoEnCola[]> {
  const todas = await leer<FotoEnCola[]>((a) => a.getAll())
  return todas.sort((a, b) => a.creado - b.creado)
}

/** Cuántas fotos de una orden esperan subir. */
export async function cuantasEnCola(ordenServicioId: string): Promise<number> {
  return (await fotosEnCola(ordenServicioId)).length
}

/** La saca de la cola: se llama cuando ya subió, o cuando el técnico la quita. */
export async function quitarDeCola(id: string): Promise<void> {
  await conEscritura((a) => a.delete(id))
}
